import React from 'react';
import type { StylesConfig } from 'react-select-5';
import * as Collapsible from '@radix-ui/react-collapsible';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import SPAButton from '../../shared/SPAButton';
import SPASelect from '../../shared/SPASelect';
import { SPANavSlot } from '../../shared/SPANav';
import {
  LessonAuditSummary,
  LIBRARIES,
  LibraryOption,
  MaterialAuditSummary,
  lessonLabel,
} from './contentAudit.types';
import { ProgramOption, groupByUnit } from './contentAudit.utils';

interface CollectionOption {
  label: string;
  value: string;
}

// Program titles run long — let the menu options wrap instead of truncating to one line.
const programSelectStyles: StylesConfig<ProgramOption, false> = {
  option: base => ({ ...base, textOverflow: 'clip', whiteSpace: 'normal' }),
};

const COLLECTION_OPTIONS: CollectionOption[] = [
  { label: 'Lessons', value: 'lessons' },
  ...LIBRARIES.map(library => ({ label: library.label, value: library.tag })),
];

const rowButtonClass = (isActive: boolean) => cn(
  'tw-min-w-0 tw-flex-1 tw-text-left tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm',
  'tw-border-0 tw-cursor-pointer tw-flex tw-flex-col tw-gap-0.5',
  isActive
    ? 'tw-bg-spa-accent-bg tw-text-spa-accent-text-hover tw-font-medium'
    : 'tw-bg-transparent tw-text-spa-primary hover:tw-bg-spa-bg-tertiary',
);

const runButtonClass = cn(
  'tw-shrink-0 tw-self-start tw-mt-1.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-border-0 tw-bg-transparent',
  'tw-cursor-pointer tw-text-xs tw-font-semibold tw-text-spa-accent-text',
  'hover:tw-text-spa-accent-text-hover hover:tw-underline',
);

interface RowProps {
  auditable: boolean;
  auditing: boolean;
  hasReport: boolean;
  label: string;
  mustEditCount: number;
  riskCount: number;
  selected: boolean;
  onRun: () => void;
  onSelect: () => void;
}

// Audited rows get an outcome dot (green = no required edits, red = has must-edits); not-run rows
// get a hollow ring so unreviewed content reads as pending at a glance.
const statusDotClass = (hasReport: boolean, mustEditCount: number) => cn(
  'tw-inline-block tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-box-border',
  hasReport && (mustEditCount > 0 ? 'tw-bg-spa-error' : 'tw-bg-spa-success'),
  !hasReport && 'tw-border tw-border-solid tw-border-spa-tertiary tw-bg-transparent',
);

const LoadingRows = () => (
  <div aria-hidden className="tw-flex tw-flex-col tw-gap-2 tw-px-2 tw-py-2">
    {[0, 1, 2, 3, 4, 5].map(row => (
      <div key={row} className="tw-h-10 tw-animate-pulse tw-rounded-md tw-bg-spa-bg-skeleton" />
    ))}
  </div>
);

const RowSpinner = () => (
  <span
    aria-hidden
    className={cn(
      'tw-inline-block tw-h-3 tw-w-3 tw-animate-spin tw-rounded-full',
      'tw-border-2 tw-border-solid tw-border-spa-border tw-box-border',
    )}
    style={{ borderTopColor: 'var(--spa-accent)' }}
  />
);

const AuditRow = ({
  auditable, auditing, hasReport, label, mustEditCount, riskCount, selected, onRun, onSelect,
}: RowProps) => (
  <div className="tw-flex tw-gap-1">
    <button
      aria-current={selected ? 'true' : undefined}
      className={rowButtonClass(selected)}
      type="button"
      onClick={onSelect}
    >
      <span className="tw-truncate">{label}</span>
      <span
        className={cn(
          'tw-flex tw-items-center tw-gap-1.5 tw-text-xs',
          selected ? 'tw-text-spa-accent-text' : 'tw-text-spa-secondary',
        )}
      >
        {auditing && (
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-spa-accent-text">
            <RowSpinner />
            Auditing…
          </span>
        )}
        {!auditing && !hasReport && !auditable && 'No text to audit'}
        {!auditing && !hasReport && auditable && (
          <>
            <span aria-hidden className={statusDotClass(false, 0)} />
            Not run
          </>
        )}
        {!auditing && hasReport && (
          <>
            <span aria-hidden className={statusDotClass(true, mustEditCount)} />
            <span>
              {riskCount} flagged
              {' · '}
              {/* Echo the report pane's Must edit color so urgent rows read at a glance. */}
              <span className={mustEditCount > 0 ? 'tw-text-spa-error' : undefined}>
                {mustEditCount} must edit
              </span>
            </span>
          </>
        )}
      </span>
    </button>
    {!auditing && auditable && (
      <button aria-label={`Run audit for ${label}`} className={runButtonClass} type="button" onClick={onRun}>
        {hasReport ? 'Re-run' : 'Run'}
      </button>
    )}
  </div>
);

interface Props {
  auditingIds: number[];
  batchQueuing: boolean;
  batchResult: string | null;
  collection: string;
  flOnly: boolean;
  lessons: LessonAuditSummary[];
  library: LibraryOption | null;
  materials: MaterialAuditSummary[];
  notRunCount: number;
  program: ProgramOption | null;
  programOptions: ProgramOption[];
  programsLoading: boolean;
  railState: 'error' | 'loaded' | 'loading';
  selectedLessonId: number | null;
  selectedMaterialId: number | null;
  onChangeCollection: (value: string) => void;
  onChangeProgram: (option: ProgramOption | null) => void;
  onRunBatch: () => void;
  onRunRow: (id: number) => void;
  onSelectLesson: (id: number) => void;
  onSelectMaterial: (id: number) => void;
  onToggleFlOnly: () => void;
}

const AuditSidebar = ({
  auditingIds,
  batchQueuing,
  batchResult,
  collection,
  flOnly,
  lessons,
  library,
  materials,
  notRunCount,
  program,
  programOptions,
  programsLoading,
  railState,
  selectedLessonId,
  selectedMaterialId,
  onChangeCollection,
  onChangeProgram,
  onRunBatch,
  onRunRow,
  onSelectLesson,
  onSelectMaterial,
  onToggleFlOnly,
}: Props) => {
  const railEmpty = library ? materials.length === 0 : lessons.length === 0;

  const controls = (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <SPASelect
        inputId="content-audit-program"
        isLoading={programsLoading}
        name="content-audit-program"
        options={programOptions}
        placeholder="Search programs..."
        styles={programSelectStyles}
        value={program}
        onChange={option => onChangeProgram(option as ProgramOption | null)}
      />
      {program && (
      <SPASelect
        inputId="content-audit-collection"
        name="content-audit-collection"
        options={COLLECTION_OPTIONS}
        value={COLLECTION_OPTIONS.find(option => option.value === collection)}
        onChange={option => onChangeCollection((option as CollectionOption).value)}
      />
      )}
      {program && library && (
      <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-spa-primary tw-cursor-pointer">
        <input
          checked={flOnly}
          className="tw-m-0 tw-cursor-pointer"
          type="checkbox"
          onChange={onToggleFlOnly}
        />
        Florida-visible only
      </label>
      )}
      {program && !railEmpty && (
      <SPAButton
        className="tw-w-full"
        disabled={batchQueuing || notRunCount === 0}
        variant="outline"
        onClick={onRunBatch}
      >
        {batchQueuing ? 'Queuing…' : `Run missing audits${notRunCount > 0 ? ` (${notRunCount})` : ''}`}
      </SPAButton>
      )}
      {batchResult && <div className="tw-text-xs tw-text-spa-secondary">{batchResult}</div>}
    </div>
  );

  const body = (
    <nav className="tw-px-2 tw-pt-1 tw-pb-3">
      {!program && (
      <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-spa-secondary">
        Pick a program to load its lessons and libraries.
      </div>
      )}
      {program && railState === 'loading' && <LoadingRows />}
      {program && railState === 'error' && (
      <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-spa-error">
        Could not load this collection. Refresh to try again.
      </div>
      )}
      {program && railState === 'loaded' && railEmpty && (
      <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-spa-secondary">
        {library ? 'No materials in this library for this program.' : 'No lessons in this program.'}
      </div>
      )}

      {program && !library && groupByUnit(lessons).map(group => (
        <Collapsible.Root key={group.unitTitle ?? `no-unit-${group.lessons[0].lesson_id}`} defaultOpen>
          {group.unitTitle && (
          <Collapsible.Trigger
            className={cn(
              'tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-mt-2',
              'tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-secondary',
              'tw-border-0 tw-bg-transparent tw-cursor-pointer tw-rounded-md hover:tw-bg-spa-bg-tertiary',
            )}
          >
            {group.unitTitle}
            <Icon icon="ChevronDown" size={14} />
          </Collapsible.Trigger>
          )}
          <Collapsible.CollapsibleContent className="CollapsibleContent">
            {group.lessons.map(lesson => (
              <AuditRow
                key={lesson.lesson_id}
                auditable
                auditing={auditingIds.includes(lesson.lesson_id)}
                hasReport={lesson.has_report}
                label={lessonLabel(lesson)}
                mustEditCount={lesson.must_edit ?? 0}
                riskCount={lesson.risk_count ?? 0}
                selected={lesson.lesson_id === selectedLessonId}
                onRun={() => onRunRow(lesson.lesson_id)}
                onSelect={() => onSelectLesson(lesson.lesson_id)}
              />
            ))}
          </Collapsible.CollapsibleContent>
        </Collapsible.Root>
      ))}

      {program && library && materials.map(material => (
        <AuditRow
          key={material.reference_material_id}
          auditable={material.auditable !== false}
          auditing={auditingIds.includes(material.reference_material_id)}
          hasReport={material.has_report}
          label={material.title}
          mustEditCount={material.must_edit ?? 0}
          riskCount={material.risk_count ?? 0}
          selected={material.reference_material_id === selectedMaterialId}
          onRun={() => onRunRow(material.reference_material_id)}
          onSelect={() => onSelectMaterial(material.reference_material_id)}
        />
      ))}
    </nav>
  );

  return (
    <SPANavSlot header={{ backLabel: 'Dev', backTo: '/dev', title: 'Content Audit' }}>
      <div className="tw-box-border tw-p-3 tw-pb-1">{controls}</div>
      {body}
    </SPANavSlot>
  );
};

export default AuditSidebar;
